import { firstValueFrom } from "rxjs";
import { Editor, type EditorEvent, type Events, type Ui } from "tinymce";
import { NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import * as FlatModels from '@medsurf/flat-models';
import { LinkEditorDialogComponent } from '../../dialog/link-editor-dialog/link-editor-dialog.component';

export const register = (
  editor: Editor,
  zone: NgZone,
  translate: TranslateService,
  dialogService: DialogService
) => {
  const nodeChangedCallback = (
    buttonApi: Ui.Toolbar.ToolbarToggleButtonInstanceApi,
    event: EditorEvent<Events.NodeChangeEvent>
  ) => {
    const element = (<HTMLElement>event.element);
    const isAnchor = element?.tagName?.toLowerCase() === 'a';
    const isInternalLink = element?.classList?.contains('mds-internal');
    const isExternalLink = element?.classList?.contains('mds-external');
    const hasSelectionContent = !!editor.selection.getContent();

    const enable = isAnchor && (isInternalLink || isExternalLink) && !hasSelectionContent || hasSelectionContent && !/<(\bvideo\b|\baudio\b|\bimg\b|\ba\b)[\s\S]/i.test(editor.selection.getContent()) && (!isAnchor || isInternalLink || isExternalLink);

    buttonApi.setEnabled(enable);
    buttonApi.setActive(enable && (isInternalLink || isExternalLink) && isAnchor);
  };
  
  const onSetup = (buttonApi: Ui.Toolbar.ToolbarToggleButtonInstanceApi) => {
    editor.on('NodeChange', (event) => nodeChangedCallback(buttonApi, event));
    return () => {
      editor.off('NodeChange', (event) => nodeChangedCallback(buttonApi, event));
    };
  };
  
  const onAction = () => {
    const node = <HTMLAnchorElement>editor.selection.getNode();
    const isLinkNode = !!Array.from(node.classList).find((element: string) => element === 'mds-internal' || element === 'mds-external');
    const selection: string = editor.selection.getContent();
    let innerText: string = selection === node.innerText ? node.innerText : selection;
    if (!innerText) {
      innerText = node.innerText;
    }

    try {
      zone.run(async () => {
        editor.editorManager.focusedEditor = null;
        const ref = dialogService.open(LinkEditorDialogComponent, {
          header: translate.instant('global.link_edit'),
          data: {
            href: node?.getAttribute('href'),
            targetBlank: node?.target === '_blank'
          },
          width: 'min(90%, 1000px)',
          maximizable: true,
        });
        
        const result = await firstValueFrom<FlatModels.EditorAuthorModels.LinkEditorDialog>(ref.onClose);
        editor.editorManager.focusedEditor = editor;

        if (!result) return;
        const href = result.href;
        const target = result?.targetBlank ? '_blank' : '_self';
        const className = result?.type === 'internal' ? 'mds-internal' : 'mds-external';
        
        if (isLinkNode) {
          if (node?.classList?.contains(className) && node?.getAttribute('href') === href && node?.target === target) return;

          const classList = Array.from(node.classList).filter(c => c !== 'mds-internal' && c !== 'mds-external');
          classList.push(className);
          
          editor.dom.setAttribs(node, {
            target: target === '_self' ? '' : target,
            href: result.href,
            className: classList.join(' '),
          });
        } else {
          const element = document.createElement('a');
          element.target = target;
          element.href = result.href;
          element.className = className
          element.innerHTML = innerText;
          editor.insertContent(element.outerHTML);
        }
      });
    } catch (err) {
      return;
    }
  }

  editor.ui.registry.addToggleButton('link', {
    icon: 'link',
    tooltip: 'Insert/edit link',
    onAction,
    onSetup
  });
}